import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { getMsalInstance } from "./msalInstance";
import { tokenAcquisitionSuccess } from "../../store/slices/authSlice";
import store from "../../store";

// const MOBILE_SCOPE = "e3a34560-5d71-4055-b2d7-6ab366704411/.default";
const MOBILE_SCOPE = "2db662d1-27f1-432f-89b3-3efa2309d2dc/.default";
const MOBILE_SELECTED_ACCOUNT_KEY = "msal_mobile_selected_account_id";

// Global variables to store cached token information
let cachedToken: string | null = null;
let tokenExpiryTime: number | null = null;
let refreshTimer: NodeJS.Timeout | null = null;

/**
 * Clear the refresh timer
 */
const clearRefreshTimer = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

/**
 * Schedule automatic token refresh
 */
const scheduleTokenRefresh = (expiryTime: number) => {
  clearRefreshTimer();
  
  // Calculate when to refresh (10 minutes before expiry)
  const refreshTime = expiryTime - Date.now() - (10 * 60 * 1000);
  
  // Only schedule if refresh time is positive and reasonable (not more than 24 hours)
  if (refreshTime > 0 && refreshTime < 24 * 60 * 60 * 1000) {
    console.log(`Scheduling token refresh in ${Math.floor(refreshTime / 1000 / 60)} minutes`);
    refreshTimer = setTimeout(async () => {
      try {
        console.log("Automatic token refresh triggered");
        await acquireTokenSilent(true);
      } catch (error) {
        console.warn("Automatic token refresh failed:", error);
      }
    }, refreshTime);
  }
};

/**
 * Check if we have a valid cached token that hasn't expired
 */
export const hasValidCachedToken = (): boolean => {
  if (!cachedToken || !tokenExpiryTime) {
    return false;
  }
  // Check if token is still valid (with 5 minute buffer)
  const now = Date.now();
  const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
  return now < tokenExpiryTime - bufferTime;
};

/**
 * Get the cached token if it's still valid
 */
export const getCachedToken = (): string | null => {
  if (hasValidCachedToken()) {
    return cachedToken;
  }
  return null;
};

/**
 * Store token in cache with expiry information
 */
export const setCachedToken = (token: string, expiryTime?: number) => {
  cachedToken = token;
  if (expiryTime) {
    tokenExpiryTime = expiryTime;
    // Schedule automatic refresh
    scheduleTokenRefresh(expiryTime);
  } else {
    // Default to 1 hour expiry if not provided
    const defaultExpiryTime = Date.now() + 60 * 60 * 1000;
    tokenExpiryTime = defaultExpiryTime;
    scheduleTokenRefresh(defaultExpiryTime);
  }
};

/**
 * Clear the cached token
 */
export const clearCachedToken = () => {
  cachedToken = null;
  tokenExpiryTime = null;
  clearRefreshTimer();
};

/**
 * Helper function to extract expiry time from token response
 */
const extractExpiryTime = (response: any): number => {
  let expiryTime: number;
  if (
    response.expiresOn &&
    typeof response.expiresOn === "object" &&
    "getTime" in response.expiresOn
  ) {
    expiryTime = (response.expiresOn as Date).getTime();
  } else if (typeof response.expiresOn === "number") {
    expiryTime = response.expiresOn;
  } else if (typeof response.expiresOn === "string") {
    expiryTime = new Date(response.expiresOn).getTime();
  } else {
    expiryTime = Date.now() + 60 * 60 * 1000; // Default to 1 hour
  }
  return expiryTime;
};

const acquireTokenSilent = async (forceRefresh: boolean = false, preferredAccountIdentifier?: string) => {
  try {
    // If we don't need to force refresh and have a valid cached token, return it
    if (!forceRefresh && hasValidCachedToken()) {
      console.log("✅ Returning cached token");
      return cachedToken;
    }

    console.log("🔄 Acquiring new token, forceRefresh:", forceRefresh);
    
    // Use the singleton MSAL instance
    const msalInstance = await getMsalInstance();

    if (Platform.OS === "web") {
      const accounts = msalInstance.getAllAccounts();
      
      if (accounts.length > 0) {
        // Set active account if not already set
        const activeAccount = msalInstance.getActiveAccount();
        if (!activeAccount) {
          msalInstance.setActiveAccount(accounts[0]);
        }

        const request = {
          // Use API-specific scope for API access
          // This scope should match what's registered in Azure AD
          // Using client ID with /.default to request all configured scopes
          scopes: [process.env.MSAL_CLIENT_ID + "/.default"],
          account: activeAccount || accounts[0],
          forceRefresh: forceRefresh,
        };
        
        try {
          const response = await msalInstance.acquireTokenSilent(request);
          if (response && response.accessToken) {
            const expiryTime = extractExpiryTime(response);
            setCachedToken(response.accessToken, expiryTime);
            console.log("✅ Successfully acquired token silently for web");
            return response.accessToken;
          } else {
            console.warn("⚠️ No access token returned from silent acquisition");
            return null;
          }
        } catch (error: any) {
          console.warn("❌ Silent token acquisition failed:", error);
          
          // Check if interaction is required or consent needed
          if (
            error.name === "InteractionRequiredAuthError" ||
            error.message?.includes("interaction_required") ||
            error.message?.includes("consent_required") ||
            error.message?.includes("login_required") ||
            error.message?.includes("invalid_grant") ||
            error.message?.includes("AADSTS50196") // Client request loop
          ) {
            console.log("🔄 Interaction required for web token acquisition");
            return null;
          }
          
          // For other errors, throw them
          throw error;
        }
      } else {
        console.warn("👤 No accounts found for web platform");
        return null;
      }
    } else {
      // Mobile platform - keep existing logic
      const accounts = await msalInstance.getAccounts();
      
      if (accounts.length > 0) {
        const storedAccountIdentifier = preferredAccountIdentifier || await AsyncStorage.getItem(MOBILE_SELECTED_ACCOUNT_KEY);
        const preferredAccount = storedAccountIdentifier
          ? accounts.find((entry) => entry.identifier === storedAccountIdentifier)
          : undefined;
        const selectedAccount = preferredAccount || accounts[0];

        const request = {
          scopes: [MOBILE_SCOPE],
          account: selectedAccount,
          forceRefresh: forceRefresh,
        };
        
        try {
          const response = await msalInstance.acquireTokenSilent(request);
          if (response && response.accessToken) {
            const expiryTime = extractExpiryTime(response);
            setCachedToken(response.accessToken, expiryTime);
            await AsyncStorage.setItem(MOBILE_SELECTED_ACCOUNT_KEY, response.account?.identifier || selectedAccount.identifier);
            // Dispatch token to Redux store for mobile to trigger AppWrapper API calls
            store.dispatch(tokenAcquisitionSuccess(response.accessToken));
            console.log("✅ Successfully acquired token silently for mobile");
            return response.accessToken;
          } else {
            console.warn("⚠️ No access token returned for mobile");
            return null;
          }
        } catch (error: any) {
          console.warn("❌ Silent token acquisition failed for mobile:", error);
          
          // Check for common interaction required error patterns
          if (
            error.name === "InteractionRequiredAuthError" ||
            error.message?.includes("interaction_required") ||
            error.message?.includes("consent_required") ||
            error.message?.includes("login_required") ||
            error.message?.includes("invalid_grant") ||
            error.message?.includes("refresh_token_expired")
          ) {
            console.log("🔄 Interaction required for mobile token acquisition");
            return null;
          }
          throw error;
        }
      } else {
        console.warn("👤 No accounts found for mobile platform");
        return null;
      }
    }
  } catch (error) {
    console.error("❌ Error acquiring token silently:", error);
    clearCachedToken();
    throw error;
  }
};

export default acquireTokenSilent;